import { useState, useEffect, useDeferredValue, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { customerService } from '@/lib/packages';
import { VisitService } from '@/lib/visit-service';
import { Layout } from '@/components/layout/Layout';
import { AddCustomerModal } from '@/components/customers/AddCustomerModal';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, Eye, Check, X, ArrowLeft, UserCheck, Plus, Trash2, BarChart3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const FACE_PAGE_SIZE = 20;
const UNKNOWN_CUSTOMER_RETENTION_DAYS = 7;
const UNKNOWN_CUSTOMER_RETENTION_MS = UNKNOWN_CUSTOMER_RETENTION_DAYS * 24 * 60 * 60 * 1000;

interface Face {
  id: string;
  company_id: string;
  created_at: string;
  visit_id: string;
  customer_id: string | null;
  quality_score: number | null;
  src_image_path: string | null;
  meta: any;
}

interface Customer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
}

interface LookalikeCandidate {
  customer_id: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
  similarity: number;
  template_id: string;
  template_snapshot_path: string | null;
  template_count?: number;
}

const LOOKALIKE_MIN_SIMILARITY = 0.65;
const MAX_CUSTOMER_RESULTS = 200;
const TREND_QUERY_PAGE_SIZE = 1000;
interface MonthlyTrendPoint {
  month: string;
  monthKey: string;
  known: number;
  unknown: number;
}

const getMonthKey = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (date: Date): string => {
  return date.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
};

const normalizeSearchText = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/[ıİ]/g, 'i')
    .replace(/[şŞ]/g, 's')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[üÜ]/g, 'u')
    .replace(/[öÖ]/g, 'o')
    .replace(/[çÇ]/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}\s@.+-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizePhoneForSearch = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

const getExpiryTimeMs = (createdAt: string): number => {
  return new Date(createdAt).getTime() + UNKNOWN_CUSTOMER_RETENTION_MS;
};

const getRemainingTimeLabel = (createdAt: string, nowMs: number): string => {
  const remainingMs = getExpiryTimeMs(createdAt) - nowMs;
  if (remainingMs <= 0) return 'Süresi doldu';

  const totalMinutes = Math.floor(remainingMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}g ${hours}s`;
  if (hours > 0) return `${hours}s ${minutes}dk`;
  return `${Math.max(minutes, 1)}dk`;
};

const UnknownCustomers = () => {
  const navigate = useNavigate();
  const { companyId, isDemoAccount } = useAuth();
  const { toast } = useToast();
  const [faces, setFaces] = useState<Face[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFace, setSelectedFace] = useState<Face | null>(null);
  const [classificationDialog, setClassificationDialog] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deletingVisitId, setDeletingVisitId] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [lookalikes, setLookalikes] = useState<Record<string, LookalikeCandidate[]>>({});
  const [lookalikeImageUrls, setLookalikeImageUrls] = useState<Record<string, string>>({});
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [visibleFaceCount, setVisibleFaceCount] = useState(FACE_PAGE_SIZE);
  const [nowMs, setNowMs] = useState(Date.now());
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const refreshFacesPreservingContext = async () => {
    const scrollTop = window.scrollY;
    await Promise.all([
      fetchUnknownFaces({ preserveVisibleCount: true }),
      loadMonthlyTrend(),
    ]);
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollTop, behavior: 'auto' });
    });
  };

  // Fetch lookalike suggestions for a visit_face using pgvector similarity
  const fetchLookalikes = async (visitFaceId: string): Promise<LookalikeCandidate[]> => {
    try {
      const { data, error } = await supabase.rpc('find_visit_lookalikes' as any, {
        p_visit_face_id: visitFaceId,
        p_limit: 5,
      });
      if (error) {
        console.warn('[UnknownCustomers] lookalikes RPC error:', error.message);
        return [];
      }
      return ((data || []) as LookalikeCandidate[])
        .map((candidate) => ({
          ...candidate,
          similarity: Math.min(1, Math.max(0, Number(candidate.similarity) || 0)),
        }))
        .filter((candidate) => candidate.similarity >= LOOKALIKE_MIN_SIMILARITY)
        .sort((a, b) => b.similarity - a.similarity);
    } catch (err) {
      console.warn('[UnknownCustomers] lookalikes fetch failed:', err);
      return [];
    }
  };

  // Load lookalikes for currently visible faces only.
  const loadLookalikesForFaces = async (facesList: Face[]) => {
    if (facesList.length === 0) return;
    const results: Record<string, LookalikeCandidate[]> = {};
    const thumbnailUrlsByCustomerId: Record<string, string> = {};
    const realFaces = facesList.filter((face) => !face.id.startsWith('visit-') && !lookalikes[face.id]);
    if (realFaces.length === 0) return;
    await Promise.all(
      realFaces.map(async (face) => {
        const candidates = await fetchLookalikes(face.id);
        if (candidates.length > 0) {
          results[face.id] = candidates;
        }
      })
    );

    const thumbnailPathsByCustomerId = new Map<string, string>();
    Object.values(results).forEach((candidateList) => {
      candidateList.forEach((candidate) => {
        if (candidate.template_snapshot_path && !thumbnailPathsByCustomerId.has(candidate.customer_id)) {
          thumbnailPathsByCustomerId.set(candidate.customer_id, candidate.template_snapshot_path);
        }
      });
    });

    await Promise.all(
      Array.from(thumbnailPathsByCustomerId.entries()).map(async ([customerId, snapshotPath]) => {
        const signedUrl = await getImageUrl(snapshotPath);
        if (signedUrl) {
          thumbnailUrlsByCustomerId[customerId] = signedUrl;
        }
      })
    );

    if (Object.keys(results).length > 0) {
      setLookalikes((prev) => ({ ...prev, ...results }));
    }
    if (Object.keys(thumbnailUrlsByCustomerId).length > 0) {
      setLookalikeImageUrls((prev) => ({ ...prev, ...thumbnailUrlsByCustomerId }));
    }
  };

  // Fetch unknown faces (where customer_id is null and owner_type is 'unknown')
  const fetchUnknownFaces = async (options?: { preserveVisibleCount?: boolean }) => {
    if (!companyId) return;

    try {
      console.log('[UnknownCustomers] fetchUnknownFaces:start', { companyId });

      // Primary source: pending unknown visits from Facey bridge.
      const { data: unknownVisits, error: visitsErr } = await (supabase as any)
        .from('visits')
        .select('id, company_id, customer_id, entered_at, created_at, status')
        .eq('company_id', companyId)
        .is('customer_id', null)
        .eq('status', 'PENDING_LABEL')
        .order('entered_at', { ascending: false })
        .limit(500);

      console.log('[UnknownCustomers] visits query result', {
        error: visitsErr ? { code: visitsErr.code, message: visitsErr.message } : null,
        count: unknownVisits?.length || 0,
        sample: (unknownVisits || []).slice(0, 3).map((visit: any) => ({
          id: visit.id,
          status: visit.status,
          customer_id: visit.customer_id,
          entered_at: visit.entered_at,
        })),
      });

      if (!visitsErr && unknownVisits && unknownVisits.length > 0) {
        const visitIds = unknownVisits.map((visit: any) => visit.id);
        const visitMetaById = new Map<string, any>();
        for (const visit of unknownVisits) {
          visitMetaById.set(visit.id, visit);
        }

        const { data: visitFaceRows, error: visitFacesErr } = await (supabase as any)
          .from('visit_faces')
          .select('id, visit_id, snapshot_path, quality_score, camera_id, created_at')
          .in('visit_id', visitIds)
          .order('created_at', { ascending: false });

        console.log('[UnknownCustomers] visit_faces query result', {
          error: visitFacesErr ? { code: visitFacesErr.code, message: visitFacesErr.message } : null,
          count: visitFaceRows?.length || 0,
          sample: (visitFaceRows || []).slice(0, 3).map((face: any) => ({
            id: face.id,
            visit_id: face.visit_id,
            snapshot_path: face.snapshot_path,
            quality_score: face.quality_score,
          })),
        });

        if (!visitFacesErr) {
          const entryRows: any[] = [];
          for (const row of (visitFaceRows || [])) {
            entryRows.push(row);
          }

          const mappedFaces: Face[] = entryRows.map((row: any) => {
            const visitMeta = visitMetaById.get(row.visit_id);
            return {
              id: row.id,
              company_id: visitMeta?.company_id ?? companyId,
              created_at: row.created_at,
              visit_id: row.visit_id,
              customer_id: null,
              quality_score: row.quality_score,
              src_image_path: row.snapshot_path ?? null,
              meta: null,
            };
          });

          const visitIdsWithFace = new Set(entryRows.map((row: any) => row.visit_id));
          const syntheticFaces: Face[] = unknownVisits
            .filter((visit: any) => !visitIdsWithFace.has(visit.id))
            .map((visit: any) => ({
              id: `visit-${visit.id}`,
              company_id: visit.company_id ?? companyId,
              created_at: visit.entered_at || visit.created_at || new Date().toISOString(),
              visit_id: visit.id,
              customer_id: null,
              quality_score: null,
              src_image_path: null,
              meta: { synthetic: true, reason: 'visit_face_missing' },
            }));

          const combined = [...mappedFaces, ...syntheticFaces].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );

          console.log('[UnknownCustomers] mapped faces summary', {
            mappedFaces: mappedFaces.length,
            syntheticFaces: syntheticFaces.length,
            total: combined.length,
            syntheticVisitIds: syntheticFaces.slice(0, 10).map((face) => face.visit_id),
          });

          setFaces(combined);
          setVisibleFaceCount((prev) =>
            options?.preserveVisibleCount ? Math.max(prev, FACE_PAGE_SIZE) : FACE_PAGE_SIZE
          );
          return;
        }
      }

      // No legacy faces table — return empty list when visits query yields nothing.
      setFaces([]);
      setVisibleFaceCount((prev) =>
        options?.preserveVisibleCount ? Math.max(prev, FACE_PAGE_SIZE) : FACE_PAGE_SIZE
      );
    } catch (error) {
      console.error('Error fetching unknown faces:', error);
      toast({
        title: 'Hata',
        description: 'Bilinmeyen yüzler yüklenirken hata oluştu',
        variant: 'destructive',
      });
    }
  };

  // Fetch customers for selection (uses pagination to handle >1000 rows)
  const fetchCustomers = async () => {
    if (!companyId) return;

    try {
      // Use paginated getAllCustomers to fetch all customers
      const allCustomers = await customerService.getAllCustomers(companyId);
      
      // Map to the format needed (selecting only required fields)
      const customersData = allCustomers.map(customer => ({
        id: customer.id,
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone: customer.phone
      }));
      
      setCustomers(customersData);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const loadMonthlyTrend = useCallback(async (showLoader = true) => {
    if (!companyId) return;

    if (showLoader) {
      setTrendLoading(true);
    }
    try {
      const now = new Date();
      const firstMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      const tomorrowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      type TrendVisitRow = {
        id: string;
        status: string;
        entered_at: string | null;
        created_at: string;
      };
      const visitsForTrend: TrendVisitRow[] = [];
      let from = 0;

      while (true) {
        const to = from + TREND_QUERY_PAGE_SIZE - 1;
        const { data, error } = await (supabase as any)
          .from('visits')
          .select('id, status, entered_at, created_at')
          .eq('company_id', companyId)
          .in('status', ['LABELED', 'PENDING_LABEL'])
          .gte('created_at', firstMonth.toISOString())
          .lt('created_at', tomorrowStart.toISOString())
          .order('created_at', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to);

        if (error) {
          throw new Error(error.message);
        }

        const batch = (data || []) as TrendVisitRow[];
        if (batch.length === 0) break;

        visitsForTrend.push(...batch);
        if (batch.length < TREND_QUERY_PAGE_SIZE) break;
        from += TREND_QUERY_PAGE_SIZE;
      }

      const monthlyMap = new Map<string, MonthlyTrendPoint>();
      const monthDates: Date[] = [];

      for (let i = 0; i < 12; i += 1) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
        const monthKey = getMonthKey(monthDate);
        monthDates.push(monthDate);
        monthlyMap.set(monthKey, {
          month: formatMonthLabel(monthDate),
          monthKey,
          known: 0,
          unknown: 0,
        });
      }

      visitsForTrend.forEach((visit) => {
        const raw = visit.entered_at || visit.created_at;
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return;
        if (date < firstMonth || date >= tomorrowStart) return;

        const bucket = monthlyMap.get(getMonthKey(date));
        if (!bucket) return;
        if (visit.status === 'LABELED') bucket.known += 1;
        if (visit.status === 'PENDING_LABEL') bucket.unknown += 1;
      });

      setMonthlyTrend(monthDates.map((monthDate) => monthlyMap.get(getMonthKey(monthDate))!));
    } catch (error) {
      console.error('Error loading known/unknown monthly trend:', error);
      if (showLoader) {
        toast({
          title: 'Hata',
          description: 'Aylık trend grafiği yüklenemedi.',
          variant: 'destructive',
        });
        setMonthlyTrend([]);
      }
    } finally {
      if (showLoader) {
        setTrendLoading(false);
      }
    }
  }, [companyId, toast]);

  const loadImageUrls = async (facesList: Face[]) => {
    if (facesList.length === 0) return;
    const facesToLoad = facesList.filter((face) => face.src_image_path && !imageUrls[face.id]);
    if (facesToLoad.length === 0) return;

    const entries = await Promise.all(
      facesToLoad.map(async (face) => {
        const signedUrl = await getImageUrl(face.src_image_path);
        return signedUrl ? [face.id, signedUrl] as const : null;
      })
    );

    const nextUrls: Record<string, string> = {};
    entries.forEach((entry) => {
      if (entry) {
        const [faceId, signedUrl] = entry;
        nextUrls[faceId] = signedUrl;
      }
    });

    if (Object.keys(nextUrls).length > 0) {
      setImageUrls((prev) => ({ ...prev, ...nextUrls }));
    }
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      console.log('[UnknownCustomers] loadData:start', { companyId });
      await Promise.all([
        fetchUnknownFaces(),
        fetchCustomers(),
        loadMonthlyTrend(),
      ]);
      console.log('[UnknownCustomers] loadData:done');
      setLoading(false);
    };

    if (companyId) {
      loadData();
    }
  }, [companyId, loadMonthlyTrend]);

  useEffect(() => {
    if (!companyId) return;

    const intervalId = window.setInterval(() => {
      void loadMonthlyTrend(false);
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, [companyId, loadMonthlyTrend]);

  // Hide records older than 7 days and reveal remaining records in pages of 20.
  const activeFaces = useMemo(
    () => faces.filter((face) => getExpiryTimeMs(face.created_at) > nowMs),
    [faces, nowMs]
  );
  const displayedFaces = useMemo(
    () => activeFaces.slice(0, visibleFaceCount),
    [activeFaces, visibleFaceCount]
  );
  const hasMoreFaces = displayedFaces.length < activeFaces.length;

  useEffect(() => {
    if (displayedFaces.length > 0) {
      loadImageUrls(displayedFaces);
      loadLookalikesForFaces(displayedFaces);
    }
    console.log('[UnknownCustomers] faces state updated', {
      count: faces.length,
      withVisitId: faces.filter((face) => Boolean(face.visit_id)).length,
      withImagePath: faces.filter((face) => Boolean(face.src_image_path)).length,
      sample: faces.slice(0, 5).map((face) => ({
        id: face.id,
        visit_id: face.visit_id,
        created_at: face.created_at,
        src_image_path: face.src_image_path,
        synthetic: Boolean((face.meta as any)?.synthetic),
      })),
    });
  }, [faces, displayedFaces]);

  const handleLoadMoreFaces = () => {
    setVisibleFaceCount((prev) => prev + FACE_PAGE_SIZE);
  };

  const handleClassifyFace = (face: Face) => {
    setSelectedFace(face);
    setSelectedCustomerId('');
    setSearchTerm('');
    setClassificationDialog(true);
  };

  const handleSaveClassification = async () => {
    if (!selectedFace || !selectedCustomerId) return;

    setUpdating(true);
    try {
      if (isDemoAccount) {
        // Demo mode: remove face from local state only, skip DB
        setFaces((prev) => prev.filter((f) => f.visit_id !== selectedFace.visit_id));
      } else {
        await VisitService.linkVisitToCustomer(selectedFace.visit_id, selectedCustomerId);
      }

      toast({
        title: 'Başarılı',
        description: 'Yüz sınıflandırması güncellendi',
      });

      if (!isDemoAccount) {
        await refreshFacesPreservingContext();
      }
      setClassificationDialog(false);
    } catch (error) {
      console.error('Error updating face classification:', error);
      toast({
        title: 'Hata',
        description: 'Sınıflandırma güncellenirken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCustomerAdded = (customerId?: string) => {
    if (!customerId) {
      void fetchCustomers();
      return;
    }

    const faceToMatch = selectedFace;

    if (!faceToMatch?.visit_id) {
      void fetchCustomers();
      return;
    }

    // Close both modals immediately so user does not return to match modal.
    setShowAddCustomerModal(false);
    setClassificationDialog(false);
    setSearchTerm('');
    setSelectedCustomerId('');
    setSelectedFace(null);

    void (async () => {
      setUpdating(true);
      try {
        if (isDemoAccount) {
          setFaces((prev) => prev.filter((f) => f.visit_id !== faceToMatch.visit_id));
        } else {
          await VisitService.linkVisitToCustomer(faceToMatch.visit_id, customerId);
        }

        toast({
          title: 'Başarılı',
          description: 'Yeni müşteri oluşturuldu ve kayıt otomatik eşleştirildi.',
        });

        if (!isDemoAccount) {
          await Promise.all([refreshFacesPreservingContext(), fetchCustomers()]);
        }
      } catch (error) {
        console.error('Error auto-matching new customer:', error);
        toast({
          title: 'Hata',
          description: 'Yeni müşteri oluşturuldu ancak otomatik eşleştirme yapılamadı.',
          variant: 'destructive',
        });
        if (!isDemoAccount) await fetchCustomers();
      } finally {
        setUpdating(false);
      }
    })();
  };

  const buildStoragePathAttempts = (imagePath: string): Array<[string, string]> => {
    const publicUrlMatch = imagePath.match(/^https?:\/\/[^/]+\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/i);
    const rawPath = (publicUrlMatch?.[2] || imagePath)
      .replace(/^public\/[^/]+\//, '')
      .replace(/^sign\/[^/]+\//, '')
      .replace(/^\/+/, '');

    const strippedPath = rawPath
      .replace(/^faces\//, '')
      .replace(/^visit-faces\//, '')
      .replace(/^visit_faces\//, '');

    const attempts: Array<[string, string]> = [];
    if (publicUrlMatch?.[1]) {
      attempts.push([publicUrlMatch[1], rawPath]);
      attempts.push([publicUrlMatch[1], strippedPath]);
    }
    attempts.push(['faces', rawPath]);
    if (strippedPath !== rawPath) {
      attempts.push(['faces', strippedPath]);
    }
    attempts.push(['visit-faces', strippedPath]);
    attempts.push(['visit_faces', strippedPath]);

    const seen = new Set<string>();
    return attempts.filter(([bucket, path]) => {
      const key = `${bucket}::${path}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const handleDeleteUnknownRecord = async (face: Face) => {
    const confirmed = window.confirm(
      'Bu kayıt silinsin mi?\n\nBu işlem geri alınamaz ve ilgili snapshot/gorseller ile ziyaret kayitlari da silinir.'
    );
    if (!confirmed) return;

    if (isDemoAccount) {
      setFaces((prev) => prev.filter((f) => f.visit_id !== face.visit_id));
      toast({ title: 'Kayit silindi', description: 'Ilgili ziyaret, snapshot ve bagli kayitlar silindi.' });
      return;
    }

    setDeletingVisitId(face.visit_id);
    try {
      console.log('[UnknownCustomers] delete:start', {
        faceId: face.id,
        visitId: face.visit_id,
        snapshotPath: face.src_image_path,
      });

      const { data: beforeVisitCheck, error: beforeVisitCheckErr } = await (supabase as any)
        .from('visits')
        .select('id, status, customer_id')
        .eq('id', face.visit_id)
        .maybeSingle();
      console.log('[UnknownCustomers] delete:beforeVisitCheck', {
        visitId: face.visit_id,
        exists: Boolean(beforeVisitCheck),
        row: beforeVisitCheck || null,
        error: beforeVisitCheckErr ? beforeVisitCheckErr.message : null,
      });

      const { data: visitFaces, error: visitFacesErr } = await supabase
        .from('visit_faces')
        .select('snapshot_path')
        .eq('visit_id', face.visit_id);
      if (visitFacesErr) {
        throw new Error(`Visit face bilgileri alinamadi: ${visitFacesErr.message}`);
      }
      console.log('[UnknownCustomers] delete:visitFaces', {
        visitId: face.visit_id,
        count: visitFaces?.length || 0,
      });

      const bucketToPaths = new Map<string, Set<string>>();
      for (const row of visitFaces || []) {
        if (!row.snapshot_path) continue;
        for (const [bucket, path] of buildStoragePathAttempts(row.snapshot_path)) {
          if (!bucketToPaths.has(bucket)) bucketToPaths.set(bucket, new Set<string>());
          bucketToPaths.get(bucket)!.add(path);
        }
      }

      for (const [bucket, pathsSet] of bucketToPaths.entries()) {
        const paths = Array.from(pathsSet);
        if (paths.length === 0) continue;
        try {
          const { data: removedFiles, error: removeErr } = await supabase.storage.from(bucket).remove(paths);
          console.log('[UnknownCustomers] delete:storageRemove', {
            bucket,
            attempted: paths.length,
            removedCount: removedFiles?.length || 0,
            error: removeErr ? removeErr.message : null,
          });
        } catch (storageErr) {
          console.warn(`[UnknownCustomers] storage remove failed for bucket=${bucket}`, storageErr);
        }
      }

      const { data: deletedRows, error: deleteVisitErr } = await (supabase as any)
        .from('visits')
        .delete()
        .select('id')
        .eq('id', face.visit_id);

      console.log('[UnknownCustomers] delete:visitDeleteResult', {
        visitId: face.visit_id,
        deletedCount: deletedRows?.length || 0,
        deletedRows: deletedRows || [],
        error: deleteVisitErr ? deleteVisitErr.message : null,
      });

      if (deleteVisitErr) {
        throw new Error(`Ziyaret kaydi silinemedi: ${deleteVisitErr.message}`);
      }
      if (!deletedRows || deletedRows.length === 0) {
        throw new Error(
          'Ziyaret kaydi silinemedi: 0 satir etkilendi. Muhtemel neden: RLS/policy engeli veya kayit zaten yok.'
        );
      }

      const { data: afterVisitCheck, error: afterVisitCheckErr } = await (supabase as any)
        .from('visits')
        .select('id')
        .eq('id', face.visit_id)
        .maybeSingle();
      console.log('[UnknownCustomers] delete:afterVisitCheck', {
        visitId: face.visit_id,
        stillExists: Boolean(afterVisitCheck),
        row: afterVisitCheck || null,
        error: afterVisitCheckErr ? afterVisitCheckErr.message : null,
      });

      toast({
        title: 'Kayit silindi',
        description: 'Ilgili ziyaret, snapshot ve bagli kayitlar silindi.',
      });

      await refreshFacesPreservingContext();
      console.log('[UnknownCustomers] delete:afterRefresh', {
        visitId: face.visit_id,
      });
    } catch (error) {
      console.error('Error deleting unknown customer record:', error);
      toast({
        title: 'Hata',
        description: error instanceof Error ? error.message : 'Kayit silinirken hata olustu',
        variant: 'destructive',
      });
    } finally {
      setDeletingVisitId(null);
    }
  };

  const getImageUrl = async (imagePath: string | null): Promise<string | null> => {
    if (!imagePath) return null;

    // Pipeline stores snapshots in `faces` bucket at path `visit_faces/{date}/{id}.jpg`.
    // We try likely bucket/path combinations for compatibility with legacy paths.
    const uniqueAttempts = buildStoragePathAttempts(imagePath);

    for (const [bucket, path] of uniqueAttempts) {
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, 3600);

        if (!error && data?.signedUrl) {
          return data.signedUrl;
        }
      } catch (err) {
        // Silently try next
      }
    }

    console.error('Unable to create signed URL for image path:', imagePath);
    return null;
  };
  
  


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getCustomerName = (customer: Customer) => {
    const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    return name || 'İsimsiz Müşteri';
  };

  const searchableCustomers = useMemo(
    () =>
      customers.map((customer) => ({
        customer,
        name: normalizeSearchText(getCustomerName(customer)),
        email: normalizeSearchText(customer.email || ''),
        phoneRaw: normalizeSearchText(customer.phone || ''),
        phoneDigits: normalizePhoneForSearch(customer.phone || ''),
      })),
    [customers]
  );

  const filteredCustomers = useMemo(() => {
    const normalizedSearch = normalizeSearchText(deferredSearchTerm);
    const normalizedSearchDigits = normalizePhoneForSearch(deferredSearchTerm);
    const searchTokens = normalizedSearch.split(' ').filter(Boolean);

    if (!normalizedSearch && !normalizedSearchDigits) {
      return customers.slice(0, MAX_CUSTOMER_RESULTS);
    }

    return searchableCustomers
      .filter(({ name, email, phoneRaw, phoneDigits }) => {
        const textMatch = searchTokens.length > 0
          ? searchTokens.every((token) =>
              name.includes(token) || email.includes(token) || phoneRaw.includes(token)
            )
          : false;
        const digitMatch =
          (normalizedSearchDigits && phoneDigits.includes(normalizedSearchDigits)) || false;

        return textMatch || digitMatch;
      })
      .map(({ customer }) => customer)
      .slice(0, MAX_CUSTOMER_RESULTS);
  }, [customers, searchableCustomers, deferredSearchTerm]);

  const monthToDateTotals = useMemo(() => {
    const currentMonthKey = getMonthKey(new Date());
    const currentMonthPoint = monthlyTrend.find((point) => point.monthKey === currentMonthKey);
    const known = currentMonthPoint?.known ?? 0;
    const unknown = currentMonthPoint?.unknown ?? 0;
    return {
      known,
      unknown,
      total: known + unknown,
    };
  }, [monthlyTrend]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Bilinmeyen yüzler yükleniyor...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Bilinmeyen Müşteriler</h1>
            <p className="text-muted-foreground">
              Tanınmayan yüzleri müşterilerle eşleştirin
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="text-lg px-3 py-1">
          {activeFaces.length} Bilinmeyen Yüz
        </Badge>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Aylık Bilinen / Bilinmeyen Giriş Trendi
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Bu ay şimdiye kadar (kümülatif): {monthToDateTotals.total} giriş ({monthToDateTotals.known} bilinen, {monthToDateTotals.unknown} bilinmeyen)
          </p>
        </CardHeader>
        <CardContent>
          {trendLoading ? (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              Grafik yükleniyor...
            </div>
          ) : monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="known" name="Bilinen" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="unknown" name="Bilinmeyen" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              Trend verisi bulunamadı
            </div>
          )}
        </CardContent>
      </Card>

      {activeFaces.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Eye className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Bilinmeyen Yüz Bulunamadı</h3>
            <p className="text-muted-foreground text-center">
              Tüm yüzler sınıflandırılmış durumda. Yeni bilinmeyen yüzler tespit edildiğinde burada görünecektir.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedFaces.map((face) => (
              <Card key={face.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Bilinmeyen Yüz</CardTitle>
                  <Badge variant="outline" className="text-xs">
                    Kalite: {face.quality_score?.toFixed(1) || 'N/A'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {face.src_image_path && (
                  <div className="aspect-square overflow-hidden rounded-lg bg-muted">
                    {imageUrls[face.id] ? (
                      <img
                        src={imageUrls[face.id]}
                        alt="Bilinmeyen yüz"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          console.error('Image failed to load:', face.src_image_path, 'URL:', imageUrls[face.id]);
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDOTAuNTk0IDcwIDgzIDc3LjU5NCA4MyA4N0M4MyA5Ni40MDYgOTAuNTk0IDEwNCAxMDAgMTA0QzEwOS40MDYgMTA0IDExNyA5Ni40MDYgMTE3IDg3QzExNyA3Ny41OTQgMTA5LjQwNiA3MCAxMDAgNzBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMDAgMTEwQzg1LjY0IDExMCA3NCAxMjEuNjQgNzQgMTM2VjE0MEg3NlYxMzZDNzYgMTIyLjc0NSA4Ni43NDUgMTEyIDEwMCAxMTJDMTEzLjI1NSAxMTIgMTI0IDEyMi43NDUgMTI0IDEzNlYxNDBIMTI2VjEzNkMxMjYgMTIxLjY0IDExNC4zNiAxMTAgMTAwIDExMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                          target.alt = 'Resim yüklenemedi';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', face.src_image_path);
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tespit Tarihi:</span>
                    <span>{formatDate(face.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Kalan Süre:</span>
                    <span className="font-medium">{getRemainingTimeLabel(face.created_at, nowMs)}</span>
                  </div>
                </div>

                {/* Lookalike Suggestions */}
                {lookalikes[face.id] && lookalikes[face.id].length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Benzer Müşteriler:</p>
                    <div className="space-y-1.5">
                      {lookalikes[face.id].map((candidate) => (
                        <div
                          key={candidate.template_id}
                          className="flex items-center justify-between p-2 rounded-md border bg-muted/30 hover:bg-muted/60 cursor-pointer transition-colors"
                          onClick={async () => {
                            setUpdating(true);
                            try {
                              if (isDemoAccount) {
                                setFaces((prev) => prev.filter((f) => f.visit_id !== face.visit_id));
                              } else {
                                await VisitService.linkVisitToCustomer(face.visit_id, candidate.customer_id);
                                await refreshFacesPreservingContext();
                              }
                              toast({ title: 'Eşleştirme Yapıldı', description: `${candidate.customer_first_name || ''} ${candidate.customer_last_name || ''} ile eşleştirildi.` });
                            } catch (err) {
                              console.error('Quick match error:', err);
                              toast({ title: 'Hata', description: 'Eşleştirme sırasında hata oluştu.', variant: 'destructive' });
                            } finally {
                              setUpdating(false);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {lookalikeImageUrls[candidate.customer_id] ? (
                              <img
                                src={lookalikeImageUrls[candidate.customer_id]}
                                alt={`${candidate.customer_first_name || ''} ${candidate.customer_last_name || ''}`.trim() || 'Müşteri'}
                                loading="lazy"
                                decoding="async"
                                className="w-7 h-7 rounded-md object-cover border shrink-0"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-md border bg-muted flex items-center justify-center shrink-0">
                                <UserCheck className="w-3.5 h-3.5 text-primary" />
                              </div>
                            )}
                            <span className="text-sm truncate">
                              {`${candidate.customer_first_name || ''} ${candidate.customer_last_name || ''}`.trim() || 'Adsız'}
                            </span>
                            {(candidate.template_count || 1) > 1 && (
                              <Badge variant="outline" className="text-[10px] h-5 px-1.5 shrink-0">
                                {(candidate.template_count || 1)} yuz
                              </Badge>
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs shrink-0 ml-2">
                            %{(candidate.similarity * 100).toFixed(0)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <Button
                    onClick={() => handleClassifyFace(face)}
                    className="w-full"
                    variant="default"
                    disabled={deletingVisitId === face.visit_id}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Müşteriyle Eşleştir
                  </Button>
                  <Button
                    onClick={() => handleDeleteUnknownRecord(face)}
                    className="w-full"
                    variant="destructive"
                    disabled={deletingVisitId === face.visit_id}
                  >
                    {deletingVisitId === face.visit_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Kaydı Sil
                  </Button>
                </div>
              </CardContent>
              </Card>
            ))}
          </div>
          {hasMoreFaces && (
            <div className="mt-6 flex justify-center">
              <Button onClick={handleLoadMoreFaces} variant="outline">
                Daha Fazla Yükle (20)
              </Button>
            </div>
          )}
        </>
      )}

      {/* Classification Dialog */}
      <Dialog open={classificationDialog} onOpenChange={setClassificationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Müşteriyle Eşleştir</DialogTitle>
            <DialogDescription>
              Bilinmeyen yüzü bir müşteriyle eşleştirin. Eşleştirme sonrası bir sonraki girişte yüz tanıma otomatik çalışacaktır.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Face Image */}
            {selectedFace?.src_image_path && (
              <div className="flex justify-center">
                <div className="w-32 h-32 overflow-hidden rounded-lg bg-muted">
                  {selectedFace && imageUrls[selectedFace.id] ? (
                    <img
                      src={imageUrls[selectedFace.id]}
                      alt="Sınıflandırılacak yüz"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        console.error('Dialog image failed to load:', selectedFace.src_image_path);
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDOTAuNTk0IDcwIDgzIDc3LjU5NCA4MyA4N0M4MyA5Ni40MDYgOTAuNTk0IDEwNCAxMDAgMTA0QzEwOS40MDYgMTA0IDExNyA5Ni40MDYgMTE3IDg3QzExNyA3Ny41OTQgMTA5LjQwNiA3MCAxMDAgNzBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMDAgMTEwQzg1LjY0IDExMCA3NCAxMjEuNjQgNzQgMTM2VjE0MEg3NlYxMzZDNzYgMTIyLjc0NSA4Ni43NDUgMTEyIDEwMCAxMTJDMTEzLjI1NSAxMTIgMTI0IDEyMi43NDUgMTI0IDEzNlYxNDBIMTI2VjEzNkMxMjYgMTIxLjY0IDExNC4zNiAxMTAgMTAwIDExMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                        target.alt = 'Resim yüklenemedi';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Customer Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Müşteri Seçin</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddCustomerModal(true)}
                  className="shrink-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Müşteri Ekle
                </Button>
              </div>
              <Input
                placeholder="Müşteri ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`p-3 cursor-pointer hover:bg-muted border-b last:border-b-0 ${
                      selectedCustomerId === customer.id ? 'bg-primary/10' : ''
                    }`}
                    onClick={() => setSelectedCustomerId(customer.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{getCustomerName(customer)}</div>
                        {customer.email && (
                          <div className="text-sm text-muted-foreground">{customer.email}</div>
                        )}
                        {customer.phone && (
                          <div className="text-sm text-muted-foreground">{customer.phone}</div>
                        )}
                      </div>
                      {selectedCustomerId === customer.id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setClassificationDialog(false)}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button
                onClick={handleSaveClassification}
                disabled={updating || !selectedCustomerId}
                className="flex-1"
              >
                {updating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Customer Modal - when no matching customer exists */}
      <AddCustomerModal
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onCustomerAdded={handleCustomerAdded}
      />
    </div>
    </Layout>
  );
};

export default UnknownCustomers;
